import className from "classnames/bind";
import { useTranslation } from "next-i18next";
import React, { useState } from "react";

import { isAmazonUKSlug } from "@/utils/amazonUK";
import { isClearpay } from "@/utils/locale";

import { Locale } from "@/types/locale";

import { UI_CONTEXTS } from "../../analytics/constants";
import EVENTS from "../../analytics/events";
import LinkButton from "../../components/buttons/link-button";
import { StoreProps } from "../../interfaces/stores-interface";
import { KINDS, SIZES } from "../buttons/buttons.constants";
import { ApLogoMini } from "../icons/ap-logo-mini";
import Grid from "../layout/grid";
import { Offer } from "../modals/offer";
import { StoreTile } from "../store-tiles/store-tile";
import { AmazonUKSpecialContent } from "./amazon-uk-special-content";
import styles from "./retailerPage.module.scss";

type Props = {
    store: StoreProps;
    locale: Locale;
};

const cx = className.bind(styles);

export const RetailerPage: React.FC<Props> = ({ store, locale }) => {
    const { t } = useTranslation();
    const isAmazonUk = locale === "en-GB" && isAmazonUKSlug(store.slug);
    const showUkComplianceCopy = isClearpay(locale);
    const initialStates = { [store.id]: false };

    const [isOfferModalsVisible, setOfferModalsVisible] = useState(initialStates);
    const [isOfferModalsFading, setOfferModalsFading] = useState(initialStates);

    const offerButtonClickHandler = (store) => {
        setOfferModalsVisible((prevState) => ({ ...prevState, [store.id]: true }));
        setOfferModalsFading((prevState) => ({ ...prevState, [store.id]: true }));
    };

    const ButtonGroup = () => {
        return (
            <>
                {!store.inStoreOnly && (
                    <div className={styles.retailerPageButton}>
                        <LinkButton
                            disabled={false}
                            fluid={true}
                            kind={KINDS.PRIMARY}
                            label={t("retailerPage.buttonGroup.button1")}
                            size={SIZES.LARGE}
                            show={true}
                            url={store.merchantOutboundUrl}
                            newTab={true}
                            analytics={{
                                eventName: EVENTS.BUTTON_CLICK,
                                uiContext: UI_CONTEXTS.RETAILER_PROFILE,
                            }}
                        />
                    </div>
                )}
                {store.hasLocations && (
                    <div className={styles.retailerPageButton}>
                        <LinkButton
                            disabled={false}
                            fluid={true}
                            kind={KINDS.PRIMARY}
                            label={t("retailerPage.buttonGroup.button2")}
                            size={SIZES.LARGE}
                            show={true}
                            url={store.locationUrl}
                            newTab={true}
                            analytics={{
                                eventName: EVENTS.BUTTON_CLICK,
                                uiContext: UI_CONTEXTS.RETAILER_PROFILE,
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    const SignupBlurb = () => {
        return (
            <div className={styles.retailerPageSignupBlurb}>
                <div className={styles.logoContainer}>
                    <ApLogoMini />
                </div>
                <div className={styles.contentContainer}>
                    <h1 className={styles.contentHeading}>
                        {t("retailerPage.signupBlurb.heading")}
                    </h1>
                    <p className={styles.contentBody}>{t("retailerPage.signupBlurb.body")}</p>
                    <h2 className={styles.contentLink}>
                        {t("retailerPage.signupBlurb.contentLink")}
                    </h2>
                </div>
            </div>
        );
    };

    const TitleText = () => {
        return (
            <>
                <h1 className={styles.retailerPageTitle}>
                    {t("retailerPage.titleText.title", { merchantName: store.name })}
                </h1>
                <p className={styles.retailerPageTitleText}>
                    {t("retailerPage.titleText.text1", { merchantName: store.name })}
                </p>
                <p className={styles.retailerPageTitleText}>
                    {t("retailerPage.titleText.text2", { merchantName: store.name })}
                </p>
            </>
        );
    };

    const TextDetail = () => {
        const textDetaileSectionClassName = cx({
            [styles.textDetailSection]: true,
            [styles.hasNoLocations]: !store.hasLocations && !store.isSUP,
        });

        return (
            <div className={styles.retailerTextDetail}>
                <div className={styles.textDetailSections}>
                    {store.online ? (
                        <div className={textDetaileSectionClassName}>
                            <h2 className={styles.textDetailHeading}>
                                {t("retailerPage.textDetail.section1.heading", {
                                    merchantName: store.name,
                                })}
                            </h2>
                            <ul className={styles.textDetailList}>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section1.listItem1")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section1.listItem2")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section1.listItem3", {
                                        merchantName: store.name,
                                    })}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section1.listItem4")}
                                </li>
                            </ul>
                        </div>
                    ) : null}
                    {store.hasLocations && (
                        <div className={styles.textDetailSection}>
                            <h2 className={styles.textDetailHeading}>
                                {t("retailerPage.textDetail.section2.heading", {
                                    merchantName: store.name,
                                })}
                            </h2>
                            <ul className={styles.textDetailList}>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section2.listItem1")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.section2.listItem2")}
                                </li>

                                {locale !== "en-AU" ? (
                                    // AU has only 2 items in this section now.
                                    <>
                                        <li className={styles.textDetailListItem}>
                                            {t("retailerPage.textDetail.section2.listItem3")}
                                        </li>
                                        <li className={styles.textDetailListItem}>
                                            {t("retailerPage.textDetail.section2.listItem4")}
                                        </li>
                                        <li className={styles.textDetailListItem}>
                                            {t("retailerPage.textDetail.section2.listItem5")}
                                        </li>
                                    </>
                                ) : null}
                            </ul>
                        </div>
                    )}
                    {store.isSUP && locale === "en-GB" && (
                        <div className={styles.textDetailSection}>
                            <h2 className={styles.textDetailHeading}>
                                {t("retailerPage.textDetail.sectionSUP.heading", {
                                    merchantName: store.name,
                                })}
                            </h2>
                            <ul className={styles.textDetailList}>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.sectionSUP.listItem1")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.sectionSUP.listItem2")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.sectionSUP.listItem3")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.sectionSUP.listItem4")}
                                </li>
                                <li className={styles.textDetailListItem}>
                                    {t("retailerPage.textDetail.sectionSUP.listItem5")}
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
                {showUkComplianceCopy && (
                    <p className={styles.textDetailDisclaimer}>
                        {t("retailerPage.textDetail.section1.disclaimer")}
                    </p>
                )}
            </div>
        );
    };

    const TextBlurb = () => {
        return (
            <div className={styles.retailerPagesTextBlurb}>
                <h1 className={styles.textBlurbHeading}>{t("retailerPage.textBlurb.heading")}</h1>
                <p className={styles.textBlurbBody}>
                    {store.isSUP && locale === "en-GB" ? t("retailerPage.textBlurb.sup") : null}
                    {t("retailerPage.textBlurb.body")}
                </p>
            </div>
        );
    };

    return (
        <div className={styles.retailerPageContainer}>
            <Grid fullWidth fullHeight>
                <div className={styles.retailerPageLeftBar}>
                    <div className={styles.retailerPageStoreTile}>
                        <StoreTile
                            offerButtonClickHandler={offerButtonClickHandler}
                            store={store}
                            analytics={{ uiContext: UI_CONTEXTS.RETAILER_PROFILE }}
                        />
                    </div>
                    <ButtonGroup />
                    <SignupBlurb />
                </div>
                <div className={styles.retailerPageRightBar}>
                    <TitleText />
                    {isAmazonUk ? (
                        <AmazonUKSpecialContent showDisclaimer={showUkComplianceCopy} />
                    ) : (
                        <TextDetail />
                    )}
                    {locale.endsWith("-CA") || showUkComplianceCopy ? null : <TextBlurb />}
                </div>
            </Grid>
            {isOfferModalsVisible[store.id] && (
                <Offer
                    store={store}
                    active={isOfferModalsFading[store.id]}
                    setVisibleCallback={setOfferModalsVisible}
                    setFadingCallback={setOfferModalsFading}
                    uiContext={UI_CONTEXTS.RETAILER_PROFILE}
                />
            )}
        </div>
    );
};
